import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../lib/api";
import { PublicLayout, SectionHeading } from "../components/PublicLayout";
import { toast } from "sonner";
import { BookOpen, ShoppingCart } from "lucide-react";

export default function Store() {
  const [books, setBooks] = useState(null);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState("");
  const [checkout, setCheckout] = useState(null);
  const [ship, setShip] = useState({ name: "", email: "", address1: "", address2: "", city: "", state: "", postal_code: "", country: "United States" });
  const [params, setParams] = useSearchParams();

  useEffect(() => { api.get("/store/books").then((r) => setBooks(r.data)).catch(() => setBooks([])); }, []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 6) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/store/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Purchase complete — thank you for supporting the author!");
          setParams({});
          return;
        }
        if (data.status === "expired") return;
        setTimeout(poll, 2000);
      } catch { setTimeout(poll, 2000); }
    };
    poll();
  }, [params, setParams]);

  const buy = async (e) => {
    e.preventDefault();
    const b = checkout;
    setBusy(b.id);
    try {
      const { data } = await api.post("/store/checkout", {
        project_id: b.id, quantity: parseInt(qty[b.id] || 1), origin_url: window.location.origin, shipping: ship });
      window.location.href = data.url;
    } catch (err) { toast.error(fmtErr(err)); setBusy(""); }
  };
  const setS = (k) => (e) => setShip((s) => ({ ...s, [k]: e.target.value }));
  const shipInput = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="store-page">
        <SectionHeading eyebrow="Bookstore" title="Buy directly from our authors"
          sub="Every purchase here goes further — authors earn their full royalty share on each copy sold." />
        {books === null ? <p className="text-neutral-500">Loading...</p> : books.length === 0 ? (
          <div className="border border-dashed border-neutral-300 rounded-2xl p-16 text-center">
            <BookOpen size={28} className="mx-auto text-neutral-300 mb-4" />
            <p className="text-neutral-500">The store is being stocked — check back soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {books.map((b) => (
              <div key={b.id} data-testid={`store-book-${b.id}`}
                className="bg-white border border-neutral-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="h-56 bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {b.has_cover ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/store/cover/${b.id}`} alt={b.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center px-6">
                      <BookOpen size={24} className="mx-auto text-neutral-300 mb-2" />
                      <p className="font-heading font-bold text-neutral-700 leading-snug">{b.title}</p>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-semibold font-heading text-neutral-900 leading-snug">{b.title}</h3>
                  <p className="text-xs text-neutral-400 mt-1">by {b.author_name}{b.genre ? ` · ${b.genre}` : ""}</p>
                  {(b.store_description || b.description) && (
                    <p className="text-xs text-neutral-500 mt-2 line-clamp-3">{b.store_description || b.description}</p>
                  )}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-xl font-bold font-heading">${Number(b.store_price).toFixed(2)}</p>
                    <select data-testid={`store-qty-${b.id}`} value={qty[b.id] || 1}
                      onChange={(e) => setQty((q) => ({ ...q, [b.id]: e.target.value }))}
                      className="border border-neutral-300 rounded-md px-2 py-1 text-sm bg-white">
                      {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Qty {n}</option>)}
                    </select>
                  </div>
                  <button data-testid={`store-buy-${b.id}`} onClick={() => setCheckout(b)} disabled={busy === b.id}
                    className="mt-4 w-full py-2.5 rounded-full text-white bg-neutral-900 hover:bg-neutral-700 text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                    <ShoppingCart size={15} /> {busy === b.id ? "Redirecting..." : "Buy now"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {checkout && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4 overflow-y-auto" data-testid="shipping-modal">
          <form onSubmit={buy} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-lg w-full p-7 my-8">
            <h3 className="font-semibold font-heading text-lg">Where should we ship it?</h3>
            <p className="text-sm text-neutral-500 mt-1 mb-5">{checkout.title} × {qty[checkout.id] || 1} — ${(Number(checkout.store_price) * (qty[checkout.id] || 1)).toFixed(2)}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-neutral-600">Full name</label>
                <input data-testid="ship-name" required value={ship.name} onChange={setS("name")} className={shipInput} /></div>
              <div><label className="text-xs font-medium text-neutral-600">Email (for receipt & tracking)</label>
                <input data-testid="ship-email" type="email" required value={ship.email} onChange={setS("email")} className={shipInput} /></div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Address line 1</label>
                <input data-testid="ship-address1" required value={ship.address1} onChange={setS("address1")} className={shipInput} /></div>
              <div className="sm:col-span-2"><label className="text-xs font-medium text-neutral-600">Address line 2 (optional)</label>
                <input value={ship.address2} onChange={setS("address2")} className={shipInput} /></div>
              <div><label className="text-xs font-medium text-neutral-600">City</label>
                <input data-testid="ship-city" required value={ship.city} onChange={setS("city")} className={shipInput} /></div>
              <div><label className="text-xs font-medium text-neutral-600">State</label>
                <input value={ship.state} onChange={setS("state")} className={shipInput} /></div>
              <div><label className="text-xs font-medium text-neutral-600">ZIP / Postal code</label>
                <input data-testid="ship-zip" required value={ship.postal_code} onChange={setS("postal_code")} className={shipInput} /></div>
              <div><label className="text-xs font-medium text-neutral-600">Country</label>
                <input value={ship.country} onChange={setS("country")} className={shipInput} /></div>
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="ship-continue-button" disabled={busy === checkout.id}
                className="flex-1 py-2.5 rounded-full text-white bg-neutral-900 hover:bg-neutral-700 text-sm font-medium disabled:opacity-50">
                {busy === checkout.id ? "Redirecting to payment..." : "Continue to payment"}
              </button>
              <button type="button" data-testid="ship-cancel-button" onClick={() => setCheckout(null)}
                className="px-6 py-2.5 border border-neutral-300 rounded-full text-sm font-medium text-neutral-700 hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </PublicLayout>
  );
}
